"use client"

import * as React from "react"
import { Sliders, Check, Trash2 } from "lucide-react"
import { TabsContent } from "@/components/ui/tabs"
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card"
import { SupabaseUpload } from "@/components/SupabaseUpload"
import { UserProfile } from "@/types/workspace"
import { toast } from "@/lib/toast"

export interface DeskSettingsTabProps {
  wallpaperTab: string
  setWallpaperTab: (tab: string) => void
  mobileWallpaper: string
  setMobileWallpaper: (val: string) => void
  desktopWallpaper: string
  setDesktopWallpaper: (val: string) => void
  customWallpapers?: string[]
  setCustomWallpapers?: any
  useSeparateMobileWallpaper: boolean
  handleSaveWallpaperToDb: (d: string, m: string, sep: boolean) => void
  availableWidgets?: any[]
  setWidgetsConfig?: React.Dispatch<React.SetStateAction<any[]>>
  desktopGridCols?: number
  setDesktopGridCols?: (cols: number) => void
  desktopIconSize?: number
  setDesktopIconSize?: (size: number) => void
  desktopShowLabels?: boolean
  setDesktopShowLabels?: (show: boolean) => void
  user?: UserProfile | null
}

export function DeskSettingsTab({
  wallpaperTab,
  setWallpaperTab,
  mobileWallpaper,
  setMobileWallpaper,
  desktopWallpaper,
  setDesktopWallpaper,
  customWallpapers,
  setCustomWallpapers,
  useSeparateMobileWallpaper,
  handleSaveWallpaperToDb,
  user,
}: DeskSettingsTabProps) {
  const systemPresetIds = [
    "bold-tech",
    "https://images.unsplash.com/photo-1579546929518-9e396f3cc809?w=1600",
    "https://images.unsplash.com/photo-1618005182384-a83a8bd57fbe?w=1600"
  ]

  const uniqueCustoms = Array.from(new Set(customWallpapers || [])).filter(
    (url) => !systemPresetIds.includes(url)
  )

  const customPresets = uniqueCustoms.map((url, index) => ({
    id: url,
    name: `Personalizado ${index + 1}`,
    preview: "url",
    url: url,
    isCustom: true,
  }))

  const presets = [
    { id: "bold-tech", name: "Bold Workspace (Padrão)", preview: "url", url: "/bold-tech-wallpaper.png", isCustom: false },
    { id: "https://images.unsplash.com/photo-1579546929518-9e396f3cc809?w=1600", name: "Gradiente Abstrato", preview: "url", url: "https://images.unsplash.com/photo-1579546929518-9e396f3cc809?w=300", isCustom: false },
    { id: "https://images.unsplash.com/photo-1618005182384-a83a8bd57fbe?w=1600", name: "Glassmorphism 3D", preview: "url", url: "https://images.unsplash.com/photo-1618005182384-a83a8bd57fbe?w=300", isCustom: false },
    ...customPresets,
  ]

  const activeWallpaper = wallpaperTab === "mobile" ? mobileWallpaper : desktopWallpaper

  const handleSetWallpaper = (v: string) => {
    if (user && v.startsWith("http") && !(customWallpapers || []).includes(v)) {
      const updated = [v, ...(customWallpapers || [])]
      setCustomWallpapers?.(updated)
      localStorage.setItem(`customWallpapers:${user.id}`, JSON.stringify(updated))
    }
    if (wallpaperTab === "mobile") {
      setMobileWallpaper(v)
      if (user) {
        localStorage.setItem(`mobileWallpaper:${user.id}`, v)
        handleSaveWallpaperToDb(desktopWallpaper, v, true)
      }
    } else {
      setDesktopWallpaper(v)
      if (user) {
        localStorage.setItem(`desktopWallpaper:${user.id}`, v)
        handleSaveWallpaperToDb(v, mobileWallpaper, true)
      }
    }
  }

  const handleRemoveCustomWallpaper = (e: React.MouseEvent, urlToRemove: string) => {
    e.stopPropagation()
    const updated = (customWallpapers || []).filter((u) => u !== urlToRemove)
    setCustomWallpapers?.(updated)
    if (user) {
      localStorage.setItem(`customWallpapers:${user.id}`, JSON.stringify(updated))
    }
    if (desktopWallpaper === urlToRemove) {
      handleSetWallpaper("bold-tech")
    }
    if (mobileWallpaper === urlToRemove) {
      setMobileWallpaper("bold-tech")
    }
    toast.success("Imagem removida da sua lista.")
  }

  return (
    <TabsContent value="wallpaper-tab" className="outline-none space-y-6 animate-in fade-in duration-200">
      <div>
        <h3 className="text-lg font-bold text-zinc-900 dark:text-white flex items-center gap-2">
          <Sliders className="size-5 text-[#004EB1]" />
          Papel de Parede do Desktop
        </h3>
        <p className="text-xs text-zinc-500 dark:text-zinc-400 mt-1 font-sans">
          Personalize os papéis de parede institucionais ou pessoais para o Computador e Celular.
        </p>
      </div>

      <Card className="border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900">
        <CardHeader>
          <CardTitle className="text-sm font-bold text-zinc-800 dark:text-zinc-100 font-sans flex items-center gap-2">
            <Sliders className="size-4 text-[#004EB1]" />
            Galeria de Papéis de Parede
          </CardTitle>
          <CardDescription className="text-xs font-sans">
            Escolha ou envie um papel de parede personalizado para o Computador e Celular.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          {/* Seletor de Dispositivo */}
          <div className="flex rounded-xl bg-zinc-100 dark:bg-zinc-950 border border-zinc-200 dark:border-zinc-800 p-1 gap-1">
            <button
              type="button"
              onClick={() => setWallpaperTab("desktop")}
              className={`flex-1 text-xs font-bold py-2 rounded-lg transition-all cursor-pointer flex items-center justify-center gap-2 ${wallpaperTab === "desktop"
                ? "bg-white dark:bg-zinc-800 text-zinc-900 dark:text-white shadow-sm"
                : "text-zinc-400 hover:text-zinc-700 dark:hover:text-zinc-200"
                }`}
            >
              <span>🖥️</span> Computador
            </button>
            <button
              type="button"
              onClick={() => setWallpaperTab("mobile")}
              className={`flex-1 text-xs font-bold py-2 rounded-lg transition-all cursor-pointer flex items-center justify-center gap-2 ${wallpaperTab === "mobile"
                ? "bg-white dark:bg-zinc-800 text-zinc-900 dark:text-white shadow-sm"
                : "text-zinc-400 hover:text-zinc-700 dark:hover:text-zinc-200"
                }`}
            >
              <span>📱</span> Celular
            </button>
          </div>

          {/* Grade de Presets e Wallpapers Personalizados */}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {presets.map((preset) => {
              const isSelected = activeWallpaper === preset.id
              return (
                <div
                  key={preset.id}
                  onClick={() => handleSetWallpaper(preset.id)}
                  className={`group relative rounded-xl h-24 overflow-hidden border-2 cursor-pointer transition-all duration-200 shadow-sm ${isSelected
                    ? "border-[#004EB1] ring-2 ring-[#004EB1]/30 scale-[1.02]"
                    : "border-zinc-200 dark:border-zinc-800 hover:border-zinc-400 dark:hover:border-zinc-600"
                    }`}
                >
                  {preset.preview === "url" ? (
                    <img src={(preset as any).url} alt={preset.name} className="w-full h-full object-cover" />
                  ) : (
                    <div className={`w-full h-full ${preset.preview}`} />
                  )}
                  <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center p-2 text-center">
                    <span className="text-white text-xs font-bold">{preset.name}</span>
                  </div>
                  {isSelected && (
                    <div className="absolute top-2 right-2 bg-[#004EB1] text-white p-1 rounded-full shadow-md z-10">
                      <Check className="size-3" />
                    </div>
                  )}
                  {preset.isCustom && (
                    <button
                      type="button"
                      onClick={(e) => handleRemoveCustomWallpaper(e, preset.id)}
                      className="absolute top-2 left-2 bg-red-600 hover:bg-red-700 text-white p-1 rounded-full shadow-md z-10 opacity-80 hover:opacity-100 transition-opacity"
                      title="Remover Imagem"
                    >
                      <Trash2 className="size-3" />
                    </button>
                  )}
                </div>
              )
            })}
          </div>

          {/* Seção de Upload de Wallpaper Pessoal */}
          <div className="pt-4 border-t border-zinc-200 dark:border-zinc-800 space-y-2">
            <SupabaseUpload
              label="Adicionar Wallpaper"
              accept="image/*"
              mode="button"
              cropShape="rect"
              aspectRatio={wallpaperTab === "mobile" ? 9 / 16 : 16 / 9}
              onUploadSuccess={(url: string) => {
                handleSetWallpaper(url)
                toast.success("Plano de fundo enviado e aplicado com sucesso!")
              }}
            />
          </div>
        </CardContent>
      </Card>
    </TabsContent>
  )
}
